import { useEffect } from "react";
import { privateRoute } from "./catalogueSeo";
import {
  SITE_NAME as CONFIG_SITE_NAME,
  BASE_URL as CONFIG_BASE_URL,
  HOME_TITLE,
  META_DESCRIPTION,
  OG_IMAGE,
  canonicalUrl,
} from "../config/site";

// Re-exported so the existing importers keep working; the values themselves
// now come from src/config/site.js, which is the single source of truth.
export const SITE_NAME  = CONFIG_SITE_NAME;
export const BRAND_NAME = CONFIG_SITE_NAME;
export const BASE_URL   = CONFIG_BASE_URL;
const DEFAULT_DESC = META_DESCRIPTION;
const DEFAULT_IMG  = OG_IMAGE;

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.querySelector(selector);
  if (!el) {
    const [attrName] = selector.match(/\[([^\]]+)=/) || [];
    if (!attrName) return;
    el = document.createElement("meta");
    const parts = selector.match(/\[([^\]="]+)="([^"]+)"\]/);
    if (parts) el.setAttribute(parts[1], parts[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(schema) {
  let el = document.getElementById("ld-json");
  if (!el) {
    el = document.createElement("script");
    el.id = "ld-json";
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
}

/**
 * Sets all SEO-relevant head tags for the current page.
 *
 * @param {object} opts
 * @param {string}  opts.title       — page-specific title (appended with "| Emporia")
 * @param {string}  opts.description — meta description (≤160 chars for best display)
 * @param {string}  opts.path        — canonical path, e.g. "/suits" (defaults to current pathname)
 * @param {string}  opts.image       — absolute URL for OG image
 * @param {object}  opts.schema      — JSON-LD structured data object
 */
export function useSEO({ title, titleExact, description, path, image, schema, noindex } = {}) {
  const schemaStr = schema ? JSON.stringify(schema) : null;

  useEffect(() => {
    document.head.querySelectorAll('script[data-seo="product"], script[data-seo="breadcrumb"], script[data-seo="route"]').forEach((el) => el.remove());
    // `titleExact` is for a page whose approved title is already complete —
    // About ends in "| Kenyan Online Marketplace", so the usual suffix would
    // read "… | Kenyan Online Marketplace | Emporia".
    const fullTitle = titleExact || (title ? `${title} | ${SITE_NAME}` : HOME_TITLE);
    const desc = description || DEFAULT_DESC;
    // canonicalUrl strips the query string and normalises the trailing
    // slash. Passing window.location.pathname (not .href) already excludes
    // the query, but going through the helper keeps ONE definition of what a
    // canonical URL looks like, shared with the sitemap and the edge
    // function — two definitions is how a page ends up with two URLs.
    const url  = canonicalUrl(path || window.location.pathname);
    const img  = image || DEFAULT_IMG;

    // The per-page title, not the bare brand name. This line used to read
    // `document.title = BRAND_NAME`, so every page in the app was titled
    // "Emporia" — the computed fullTitle reached og:title and nothing else,
    // and Google had no per-page title to show even after rendering the JS.
    document.title = fullTitle;

    // Standard meta
    setMeta('meta[name="description"]',     "content", desc);
    // `follow` even when noindex: the links out of a not-found page still
    // point at real pages, and there is no reason to waste them. The edge
    // function sets the same directive for /rails/* and /search; this option
    // is for a page the SERVER cannot identify as noindex, because it is not
    // a fixed path — the catch-all route is any URL the app does not route.
    setMeta('meta[name="robots"]',          "content", (noindex || privateRoute(path || window.location.pathname)) ? "noindex, follow" : "index, follow");

    // Canonical
    setLink("canonical", url);

    // Open Graph
    setMeta('meta[property="og:type"]',        "content", "website");
    setMeta('meta[property="og:site_name"]',   "content", SITE_NAME);
    setMeta('meta[property="og:title"]',       "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:url"]',         "content", url);
    setMeta('meta[property="og:image"]',       "content", img);
    setMeta('meta[property="og:locale"]',      "content", "en_KE");

    // Twitter Card
    setMeta('meta[name="twitter:card"]',        "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]',       "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]',       "content", img);

    // JSON-LD
    if (schemaStr) setJsonLd(JSON.parse(schemaStr));
    else document.getElementById("ld-json")?.remove();

    return () => {
      if (schemaStr) {
        const el = document.getElementById("ld-json");
        if (el) el.textContent = "";
      }
    };
  }, [title, titleExact, description, path, image, schemaStr, noindex]);
}
